import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X, User, AlertCircle, Plus, Check } from 'lucide-react';
import clsx from 'clsx';
import SearchInput from './SearchInput';
import Button from './Button';
import CloseButton from './CloseButton';
import ModalLoadingOverlay from './ModalLoadingOverlay';
import Message from './Message';
import { userService } from '@services';
import { ApiError } from '@app-types';
import { formatPersonName } from '@utils/stringUtils';
import { LoadingState } from '@components/common/loading';
import styles from './MultiRoleAssignmentModal.module.css';

export interface MultiRoleAssignmentModalProps {
  contractNumber: string;
  supplierName?: string;
  roleType: 'owner' | 'manager';
  currentAssigneeIds: number[];
  division?: string; // For owner division validation
  onClose: () => void;
  onAssigned: (assigneeIds: number[]) => Promise<void>;
  dataTestId?: string;
}

const MultiRoleAssignmentModal: React.FC<MultiRoleAssignmentModalProps> = ({
  contractNumber,
  supplierName,
  roleType,
  currentAssigneeIds,
  division,
  onClose,
  onAssigned,
  dataTestId = 'multi-assignment'
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(currentAssigneeIds));
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReassignment = currentAssigneeIds.length > 0;

  // Get role-specific configuration
  const getRoleConfig = () => {
    switch (roleType) {
      case 'manager':
        return {
          fieldLabel: 'Contract Manager',
          title: isReassignment ? 'Reassign Contract Manager' : 'Assign Contract Manager',
          description:
            'You are about to assign or reassign the Contract Manager. This person will oversee the contract management process. You can assign multiple managers.',
          important: 'IMPORTANT: Contract Managers will be assigned to the contract.',
          buttonText: 'Apply Changes'
        };
      case 'owner':
        return {
          fieldLabel: 'Contract Owner',
          title: isReassignment ? 'Reassign Contract Owner' : 'Assign Contract Owner',
          description:
            'You are about to assign or reassign the Contract Owner. This person will be the primary owner of this contract. You can assign multiple owners, but they must be from the same division to avoid putting the contract on hold.',
          important: 'IMPORTANT: Contract Owners will be assigned to the contract',
          buttonText: 'Apply Changes'
        };
      default:
        throw new Error(`Unknown role type: ${roleType}`);
    }
  };

  const config = getRoleConfig();

  const { data: users, isLoading: loadingUsers } = useQuery({
    queryKey: ['users', 'contract-assignees'],
    queryFn: () => userService.getUsers()
  });

  // Filter users based on search term and division (for owners)
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    let filtered = users;

    // Filter by division for owners
    if (roleType === 'owner' && division) {
      // TODO: Add division filtering when User type includes division
      // filtered = filtered.filter(u => u.division === division);
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          formatPersonName(u).toLowerCase().includes(searchLower) ||
          u.email.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [users, searchTerm, roleType, division]);

  // Get selected users
  const selectedUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => selectedIds.has(u.id));
  }, [users, selectedIds]);

  const handleToggleUser = (userId: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedIds(newSelected);
  };

  const handleRemoveUser = (userId: number) => {
    const newSelected = new Set(selectedIds);
    newSelected.delete(userId);
    setSelectedIds(newSelected);
  };

  const handleApply = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await onAssigned(Array.from(selectedIds));
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || `Failed to assign ${roleType}s`;
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (selectedIds.size !== currentAssigneeIds.length) return true;
    return !currentAssigneeIds.every((id) => selectedIds.has(id));
  }, [selectedIds, currentAssigneeIds]);

  return createPortal(
    <div className={styles.multiAssignmentModalOverlay} data-test-id={`${dataTestId}-modal`}>
      <div className={styles.multiAssignmentModal}>
        {/* Loading Overlay */}
        {isSubmitting && <ModalLoadingOverlay message="Processing request..." />}

        <div className={styles.multiAssignmentHeader}>
          <div className={styles.headerText}>
            <h3 data-test-id={`${dataTestId}-title`}>{config.title}</h3>
          </div>
          <CloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        <div className={styles.multiAssignmentContent}>
          <div className={styles.contentLayout}>
            {/* Left Panel - Description & Contract Info */}
            <div className={styles.leftPanel}>
              <p className={styles.description}>{config.description}</p>
              <p className={styles.important}>{config.important}</p>
              <div className={styles.contractInfoBox}>
                <h4>{contractNumber}</h4>
                {supplierName && <p className={styles.supplierName}>{supplierName}</p>}
              </div>

              {/* Currently Selected Users */}
              <div className={styles.selectedSection}>
                <h5>Currently Selected {config.fieldLabel}(s):</h5>
                {selectedUsers.length > 0 ? (
                  <div className={styles.selectedUsersList}>
                    {selectedUsers.map((user) => (
                      <div key={user.id} className={styles.selectedUserCard}>
                        <div className={styles.userIcon}>
                          <User size={16} />
                        </div>
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>
                            {formatPersonName(user)}
                          </span>
                          {roleType === 'owner' && user.division_name && (
                            <span className={styles.userDivision}>{user.division_name}</span>
                          )}
                          <span className={styles.userEmail}>{user.email}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={isSubmitting}
                          title="Remove"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.noSelection}>No {config.fieldLabel.toLowerCase()}s selected</p>
                )}
              </div>
            </div>

            {/* Right Panel - Available Users */}
            <div className={styles.rightPanel}>
              <h5>Available {config.fieldLabel}s</h5>

              {/* Search Bar */}
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by name, email, or division..."
                disabled={isSubmitting}
              />

              {/* Available Users List */}
              {loadingUsers && (
                <LoadingState message={`Loading ${config.fieldLabel.toLowerCase()}s...`} />
              )}
              {!loadingUsers && filteredUsers.length > 0 && (
                <div className={styles.availableUsersList}>
                  {filteredUsers.map((user) => {
                    const isSelected = selectedIds.has(user.id);
                    const isNew = isSelected && !currentAssigneeIds.includes(user.id);

                    return (
                      <div key={user.id} className={clsx(styles.availableUserCard, isSelected && styles.selected)}>
                        <div className={styles.userIcon}>
                          <User size={16} />
                        </div>
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>
                            {formatPersonName(user)}
                          </span>
                          {roleType === 'owner' && user.division_name && (
                            <span className={styles.userDivisionBadge}>{user.division_name}</span>
                          )}
                          <span className={styles.userEmail}>{user.email}</span>
                        </div>
                        <div className={styles.userActions}>
                          {isNew && <span className={styles.addedBadge}>Added</span>}
                          <button
                            type="button"
                            className={clsx(styles.addButton, isSelected && styles.added)}
                            onClick={() => handleToggleUser(user.id)}
                            disabled={isSubmitting}
                            title={isSelected ? 'Remove' : 'Add'}
                          >
                            {isSelected ? <Check size={16} /> : <Plus size={16} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!loadingUsers && filteredUsers.length === 0 && (
                <div className="empty-state">
                  <User size={48} className="icon-muted" />
                  <p>No {config.fieldLabel.toLowerCase()}s found</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <Message variant="error" icon={<AlertCircle size={16} />}>
              {error}
            </Message>
          )}
        </div>

        <div className={styles.multiAssignmentFooter}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleApply} disabled={!hasChanges || isSubmitting}>
            {isSubmitting ? 'Applying Changes...' : config.buttonText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MultiRoleAssignmentModal;
